import type { ApiNode } from '@goalbuddy/client';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { Check, Flame, Handshake, ListChecks, Plus, Repeat, Sparkles, Sun, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { NodeRow } from '@/components/node-row';
import { Badge, Button, Card, Empty, ProgressRing, Select, Skeleton } from '@/components/ui';
import { PermissionRequests } from '@/pages/agents';
import { api } from '@/lib/api';
import { useDetail, useMe, useNodes, useSettings } from '@/lib/hooks';
import { openCapture } from '@/lib/ui-events';
import { cn, fmtDateTime } from '@/lib/utils';

const todayLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());
const RITUAL = { daily_prep: 'Daily prep', weekly_review: 'Weekly review' } as const;

function greeting() {
  const h = Number(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Europe/Berlin' }).format(
      new Date(),
    ),
  );
  return h < 5 ? 'Late night' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

function SideCard({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <span className="text-muted-fg">{icon}</span> {title}
        </h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

function RoutinesToday({ date }: { date: string }) {
  const { data: routines } = useNodes({ type: ['routine'] });
  const occ = useQueries({
    queries: (routines ?? []).map((r) => ({
      queryKey: ['occurrences', r.id, date],
      queryFn: () => api.occurrences(r.id, date, date),
    })),
  });
  const set = useMutation({
    mutationFn: (v: { id: number; status: 'done' | 'open' }) => api.setOccurrence(v.id, date, v.status),
  });
  const rows = (routines ?? [])
    .map((r, i) => ({ r, o: occ[i]?.data?.occurrences[0], stats: occ[i]?.data?.stats }))
    .filter((x) => x.o);
  if (!rows.length) return null;
  const doneCount = rows.filter((x) => x.o!.status === 'done').length;
  return (
    <section>
      <div className="mb-2 flex items-center justify-between px-1">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Repeat size={15} className="text-muted-fg" /> Routines
        </h2>
        <span className="text-xs text-muted-fg">
          {doneCount} of {rows.length}
        </span>
      </div>
      <Card className="divide-y divide-border">
        {rows.map(({ r, o, stats }) => {
          const done = o!.status === 'done';
          return (
            <button
              key={r.id}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors first:rounded-t-[var(--radius-card)] last:rounded-b-[var(--radius-card)] hover:bg-muted/60"
              onClick={() => set.mutate({ id: r.id, status: done ? 'open' : 'done' })}
            >
              <span
                className={cn(
                  'flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 transition-colors',
                  done ? 'border-ok bg-ok text-white' : 'border-muted-fg/40 text-transparent',
                )}
              >
                <Check size={13} strokeWidth={3} />
              </span>
              <span className={cn('flex-1 font-medium', done && 'text-muted-fg line-through')}>
                {r.title}
              </span>
              {stats && stats.streak > 0 && (
                <span className="flex items-center gap-1 text-xs font-medium text-sun">
                  <Flame size={14} /> {stats.streak}
                </span>
              )}
            </button>
          );
        })}
      </Card>
    </section>
  );
}

function AddFocus({ candidates, onAdd }: { candidates: ApiNode[]; onAdd: (id: number) => void }) {
  if (!candidates.length) return null;
  return (
    <Select
      className="mt-3"
      value=""
      onChange={(e) => e.target.value && onAdd(Number(e.target.value))}
      aria-label="Add a next action to focus"
    >
      <option value="">+ Add a next action to today's focus…</option>
      {candidates.map((c) => (
        <option key={c.id} value={c.id}>
          {c.title}
        </option>
      ))}
    </Select>
  );
}

export function TodayPage() {
  const date = todayLocal();
  const { data: me } = useMe();
  const { open } = useDetail();
  const { data: settings } = useSettings();
  const { data: focus, isLoading } = useQuery({ queryKey: ['focus', date], queryFn: () => api.focus(date) });
  const { data: rituals } = useQuery({ queryKey: ['upcoming', 8], queryFn: () => api.upcoming(8) });
  const { data: buddy } = useQuery({ queryKey: ['buddy', true], queryFn: () => api.buddy(true) });
  const [suggested, setSuggested] = useState<{ node_id: number; title: string; reasons: string[] }[] | null>(
    null,
  );
  const suggest = useMutation({
    mutationFn: () => api.suggestFocus(date),
    onSuccess: (r) => setSuggested(r.suggestions),
  });
  const setFocus = useMutation({
    mutationFn: (ids: number[]) => api.setFocus(date, ids, true),
    onSuccess: () => setSuggested(null),
  });
  const { data: nextActions } = useNodes({ type: ['task'], is_next_action: true });
  const items = focus?.items ?? [];
  const ids = items.map((i) => i.node.id);
  const count = settings?.focus_count ?? 5;
  const doneCount = items.filter((i) => i.node.status === 'done').length;
  const progress = items.length ? doneCount / items.length : 0;
  const upNext = (nextActions ?? []).filter((n) => !ids.includes(n.id)).slice(0, 5);
  const firstName = me?.name?.split(' ')[0] ?? '';
  const allDone = items.length > 0 && doneCount === items.length;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-5">
        <ProgressRing value={progress} size={68} stroke={8}>
          {items.length ? (
            <span className="text-base">
              {doneCount}/{items.length}
            </span>
          ) : (
            <Sun size={22} className="text-sun" />
          )}
        </ProgressRing>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold sm:text-2xl md:text-3xl">
            {greeting()}
            {firstName && `, ${firstName}`}
          </h1>
          <p className="mt-0.5 text-sm text-muted-fg">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            {' · '}
            {allDone
              ? 'All focus tasks done. Nicely done.'
              : items.length
                ? `${items.length - doneCount} focus ${items.length - doneCount === 1 ? 'task' : 'tasks'} left`
                : 'Pick what matters today'}
          </p>
        </div>
      </header>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-6">
          {suggested && (
            <Card className="animate-rise space-y-3 border-primary/40 bg-accent/40 p-4">
              <div className="flex items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Sparkles size={15} className="text-primary" /> Suggested focus
                </h2>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setSuggested(null)}>
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setFocus.mutate(suggested.map((s) => s.node_id))}
                    disabled={!suggested.length}
                  >
                    Use these
                  </Button>
                </div>
              </div>
              {!suggested.length && (
                <div className="text-sm text-muted-fg">
                  No candidates. Mark some tasks as next actions or add due dates.
                </div>
              )}
              <ol className="space-y-1.5 text-sm">
                {suggested.map((s, i) => (
                  <li key={s.node_id} className="flex gap-2">
                    <span className="font-semibold text-primary">{i + 1}</span>
                    <span>
                      {s.title} <span className="text-xs text-muted-fg">{s.reasons.join(' · ')}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          <section>
            <div className="mb-2 flex items-center justify-between gap-2 px-1">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <ListChecks size={15} className="text-muted-fg" /> Focus
                <span className="font-normal text-muted-fg">
                  {items.length}/{count}
                </span>
                {focus?.confirmed && <Badge tone="ok">confirmed</Badge>}
              </h2>
              <div className="flex gap-2">
                {items.length > 0 && !focus?.confirmed && (
                  <Button size="sm" onClick={() => setFocus.mutate(ids)}>
                    Confirm plan
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => suggest.mutate()}
                  disabled={suggest.isPending}
                >
                  <Sparkles size={13} /> Suggest
                </Button>
              </div>
            </div>
            <Card className="p-1.5">
              {isLoading && (
                <div className="space-y-2 p-2">
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                </div>
              )}
              {!isLoading && items.length === 0 && (
                <Empty
                  icon={<Sun size={20} />}
                  action={
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => suggest.mutate()}>
                        <Sparkles size={13} /> Suggest my focus
                      </Button>
                      <Button size="sm" variant="outline" onClick={openCapture}>
                        <Plus size={13} /> Capture a task
                      </Button>
                    </div>
                  }
                >
                  Nothing in today's focus yet. Let GoalBuddy suggest a few next actions, or pick your own.
                </Empty>
              )}
              {items.map((i) => (
                <NodeRow
                  key={i.node.id}
                  node={i.node}
                  size="lg"
                  extra={
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-fg/70"
                      aria-label="Remove from focus"
                      onClick={() => setFocus.mutate(ids.filter((x) => x !== i.node.id))}
                    >
                      <X size={15} />
                    </Button>
                  }
                />
              ))}
              {items.length < count && (
                <div className="px-1.5 pb-1.5">
                  <AddFocus
                    candidates={(nextActions ?? []).filter((n) => !ids.includes(n.id))}
                    onAdd={(id) => setFocus.mutate([...ids, id])}
                  />
                </div>
              )}
            </Card>
          </section>

          <RoutinesToday date={date} />
        </div>

        <aside className="space-y-4">
          {upNext.length > 0 && (
            <SideCard
              title="Up next"
              icon={<ListChecks size={15} />}
              action={
                <Link to="/next" className="text-xs font-medium text-primary hover:underline">
                  All
                </Link>
              }
            >
              <ul className="-mx-1 space-y-0.5">
                {upNext.map((n) => (
                  <li key={n.id} className="flex items-center gap-1">
                    <button
                      className="min-w-0 flex-1 truncate rounded-lg px-2 py-1.5 text-left text-sm hover:bg-muted"
                      onClick={() => open(n.id)}
                    >
                      {n.title}
                    </button>
                    {items.length < count && (
                      <button
                        className="rounded-lg p-1.5 text-muted-fg hover:bg-muted hover:text-primary"
                        aria-label="Add to focus"
                        title="Add to focus"
                        onClick={() => setFocus.mutate([...ids, n.id])}
                      >
                        <Plus size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </SideCard>
          )}
          <SideCard title="Buddy check-ins" icon={<Handshake size={15} />}>
            {!buddy?.items.length ? (
              <p className="text-sm text-muted-fg">Nothing due. Your buddy is quiet.</p>
            ) : (
              <ul className="space-y-2.5 text-sm">
                {buddy.items.map((b) => (
                  <li key={b.id}>
                    <button className="text-left font-medium hover:underline" onClick={() => open(b.id)}>
                      {b.title}
                    </button>
                    {b.buddy_note && <div className="text-xs text-muted-fg">{b.buddy_note}</div>}
                  </li>
                ))}
              </ul>
            )}
          </SideCard>
          <SideCard title="Rituals" icon={<Repeat size={15} />}>
            <div className="mb-3 flex gap-2">
              <Link
                to="/review/daily"
                className="flex-1 rounded-lg bg-accent px-3 py-2 text-center text-xs font-medium text-primary hover:brightness-95"
              >
                Daily prep
              </Link>
              <Link
                to="/review/weekly"
                className="flex-1 rounded-lg bg-muted px-3 py-2 text-center text-xs font-medium hover:brightness-95"
              >
                Weekly review
              </Link>
            </div>
            <ul className="space-y-1.5 text-sm">
              {rituals?.slice(0, 3).map((r) => (
                <li key={r.schedule_id + r.at} className="flex justify-between gap-2">
                  <span>
                    {RITUAL[r.type]} {r.overridden && <Badge tone="warn">moved</Badge>}
                  </span>
                  <span className="text-xs text-muted-fg">{fmtDateTime(r.at)}</span>
                </li>
              ))}
            </ul>
          </SideCard>
          <Card className="p-4">
            <h2 className="mb-3 text-sm font-semibold">Pending permissions</h2>
            <PermissionRequests />
          </Card>
        </aside>
      </div>
    </div>
  );
}
